import {
    apiErrorMessage,
    formatDay,
    getTimezone,
    oneSpec,
    toast,
    todayInTz,
} from '@bemor/web-kit';
import {
    AsOfBadge,
    Button,
    DateRange,
    DownloadIcon,
    ListTable,
    PageHeader,
    PublicId,
    type DateRangeValue,
    type ListColumnDef,
} from '@bemor/web-kit/ui';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { StaffActivityReport, StaffActivityRow } from '@/api/types';
import { useOne } from '@/lib/queries';

/* BM-136 (decision 2): the tenant board's staff activity: a period (days of the viewer's zone) in the URL, one row per
   staff member and a Total row, every figure the server's; the CSV. Copies the finance report's period and export. */

const PATH = '/staff/reports/staff-activity';

/** The report's query as the page reads it from the URL: the month to date by default. */
function activityQuery(params: URLSearchParams) {
    const today = todayInTz(getTimezone());
    return {
        from: params.get('from') ?? today.slice(0, 8) + '01',
        to: params.get('to') ?? today,
    };
}

/** The reads of `/reports/staff-activity`, shared with the route's prefetch. */
export const staffActivityReads = (url: URL) => ({
    report: oneSpec(PATH, activityQuery(url.searchParams)),
});

/** "1 h 30 min", "12 min", "45 s": a median wait. */
export function waitLabel(seconds: number | null): string {
    if (seconds == null) return '—';
    if (seconds < 60) return `${seconds} s`;
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

type Row = StaffActivityRow & { key: string };

const count = (n: number) => <span className="tabular-nums">{n}</span>;

export const STAFF_ACTIVITY_COLUMNS: ListColumnDef<Row>[] = [
    {
        key: 'name',
        label: 'Staff member',
        cell: (r) =>
            r.key === 'total' ? (
                <span className="font-medium">Total</span>
            ) : (
                <span className="flex flex-col items-start gap-0.5">
                    {r.name ? <span>{r.name}</span> : null}
                    <PublicId id={r.staff_id} />
                </span>
            ),
        locked: 'start',
        boardCard: true,
        noTruncate: true,
    },
    {
        key: 'items_done',
        label: 'Items done',
        cell: (r) => count(r.items_done),
        boardCard: true,
        noTruncate: true,
    },
    {
        key: 'cases_closed',
        label: 'Cases closed',
        cell: (r) => count(r.cases_closed),
        noTruncate: true,
    },
    {
        key: 'notes',
        label: 'Notes',
        cell: (r) => count(r.notes),
        noTruncate: true,
    },
    {
        key: 'audit_actions',
        label: 'Audit actions',
        cell: (r) => count(r.audit_actions),
        noTruncate: true,
    },
    {
        key: 'median_wait_seconds',
        label: 'Median wait',
        cell: (r) => (
            <span className="tabular-nums">
                {waitLabel(r.median_wait_seconds)}
            </span>
        ),
        noTruncate: true,
    },
];

export function StaffActivityPage() {
    const [params, setParams] = useSearchParams();
    const q = activityQuery(params);
    const set = (from: string | null, to: string | null) => {
        const next = new URLSearchParams(params);
        next.delete('from');
        next.delete('to');
        if (from && to) {
            next.set('from', from);
            next.set('to', to);
        }
        setParams(next, { replace: true });
    };
    const report = useOne<StaffActivityReport>(PATH, q);
    const [busy, setBusy] = useState(false);
    const exportUrl = `/api${PATH}/export?${new URLSearchParams(q).toString()}`;
    const download = async () => {
        setBusy(true);
        try {
            const response = await fetch(exportUrl, {
                headers: { Accept: 'text/csv', 'X-Timezone': getTimezone() },
                credentials: 'same-origin',
            });
            if (!response.ok) {
                toast.error('The export failed. Try again.');
                return;
            }
            const href = URL.createObjectURL(await response.blob());
            const a = document.createElement('a');
            a.href = href;
            a.download = `staff-activity-${q.from}-${q.to}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(href);
            toast.success('Export downloaded');
        } catch {
            toast.error('The export failed. Try again.');
        } finally {
            setBusy(false);
        }
    };
    const data = report.data?.data;
    const range: DateRangeValue = { from: q.from, to: q.to };
    const rows: Row[] = data
        ? [
              ...data.rows.map((r) => ({ ...r, key: r.staff_id })),
              ...(data.rows.length > 0
                  ? [
                        {
                            ...data.totals,
                            key: 'total',
                            staff_id: 'total',
                            name: 'Total',
                        },
                    ]
                  : []),
          ]
        : [];
    return (
        <div className="flex flex-col gap-4">
            <PageHeader
                title="Staff activity"
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            {`${formatDay(q.from)} to ${formatDay(q.to)}: work done, cases closed, notes, audit actions and how long items waited.`}
                        </span>
                        <AsOfBadge asOf={report.freshness.asOf} />
                    </span>
                }
                secondaryAction={
                    <Button
                        variant="outline"
                        onClick={() => void download()}
                        loading={busy}
                        data-testid="staff-activity-export"
                        data-href={exportUrl}
                    >
                        <DownloadIcon className="size-4" aria-hidden="true" />
                        Export CSV
                    </Button>
                }
            />
            <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
                <DateRange
                    label="Period"
                    value={range}
                    onChange={(v) =>
                        v && 'from' in v ? set(v.from, v.to) : set(null, null)
                    }
                    timeZone={getTimezone()}
                />
            </div>
            {report.error ? (
                <p role="alert" className="text-danger-fg text-sm">
                    {apiErrorMessage(report.error)}
                </p>
            ) : null}
            <div className="overflow-x-auto" data-testid="staff-activity-table">
                <ListTable
                    columns={STAFF_ACTIVITY_COLUMNS}
                    rows={rows}
                    rowKey={(r) => r.key}
                    stickyHeader={false}
                    loading={report.isPending}
                    emptyState={
                        <p className="text-fg-2 p-4 text-center text-sm">
                            No staff work in this period.
                        </p>
                    }
                />
            </div>
        </div>
    );
}
